import { useDispatch, useSelector } from 'react-redux';
import { selectTranslations } from '../../../App/i18n/i18nSlice';
import { RootState } from '../../../App/store';
import { useEffect, useState } from 'react';
import * as React from 'react';
import SessionService from '../../../App/SessionService';
import { RoleType, RoomType, StorageKey } from '../../../App/enums';
import Button from '../../Shared/Primitive/Button';
import SearchInput from '../../Shared/Primitive/SearchInput';
import HTTPService from '../../../App/HTTPService';
import { headers } from '../../../App/Axios';
import { setModalData } from '../../Shared/SharedSlices/ModalSlice';
import { getEventsRequest } from '../RoomServiceRequests/EventServiceRequest';
import { setRoomEventsData } from '../RoomSlices/RoomEventsSlice';
import { RoomEventItem } from '../../../Types/RoomEvent';
import RoomEventListItem from '../RoomEvents/RoomEventItem';
import { useNavigate, useSearchParams } from 'react-router';
import TutorialBubble from '../../Shared/Components/TutorialBubble';
import { Calendar } from '../../Shared/Components/SvgIcons';

type RoomEventsViewMode = 'list' | 'calendar'

const toDateKey = (dateValue: string | Date) => {
    const date = new Date(dateValue)
    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    return `${year}-${month}-${day}`
}

const getCalendarDayKeysForEvent = (event: RoomEventItem) => {
    const start = new Date(event.start_date)
    const end = event.end_date ? new Date(event.end_date) : new Date(event.start_date)

    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate())

    if (endDay < startDay) {
        return [toDateKey(startDay)]
    }

    const dayKeys: string[] = []
    const pointer = new Date(startDay)

    while (pointer <= endDay) {
        dayKeys.push(toDateKey(pointer))
        pointer.setDate(pointer.getDate() + 1)
    }

    return dayKeys
}

const RoomEvents: React.FC = () => {
    const roomEventsState = useSelector((state: RootState) => state.roomEventsSlice)
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const [searchParams] = useSearchParams()
    const isMemberInRoom = useSelector((state: RootState) => state.MemberInRoomStatusSlice.MemberInRoomStatusSlice.isMember)


    const dispatch = useDispatch()
    const [isOrgAdmin, setIsOrgAdmin] = React.useState(false)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const organizationDataState = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)


    const orgId = SessionService.getItem(StorageKey.organizationId)
    const translation = useSelector(selectTranslations)
    const [loading, setLoading] = useState(false)
    const [pageNum, setPageNum] = useState(1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [intialLoadDone, setInitialLoadDone] = useState(false)
    const [loadingFiles, setLoadingFiles] = useState(false)
    const [canCreateEvents, setCanCreateEvents] = useState(false)
    const [viewMode, setViewMode] = useState<RoomEventsViewMode>('list')
    const [calendarMonth, setCalendarMonth] = useState(() => new Date())
    const [expandedCalendarDay, setExpandedCalendarDay] = useState<string | null>(null)
    const [mobileSelectedCalendarDay, setMobileSelectedCalendarDay] = useState<string | null>(null)
    const navigate = useNavigate()
    const hasSearchQuery = roomEventsState.filter.query.trim().length > 0
    const calendarEventsByDay = React.useMemo(() => {
        const groupedEvents = new Map<string, RoomEventItem[]>()

        roomEventsState.items.forEach((event) => {
            const eventDayKeys = getCalendarDayKeysForEvent(event)

            eventDayKeys.forEach((key) => {
                const existingEvents = groupedEvents.get(key) ?? []
                existingEvents.push(event)
                groupedEvents.set(key, existingEvents)
            })
        })

        groupedEvents.forEach((events, key) => {
            groupedEvents.set(key, [...events].sort((firstEvent, secondEvent) => {
                return new Date(firstEvent.start_date).getTime() - new Date(secondEvent.start_date).getTime()
            }))
        })

        return groupedEvents
    }, [roomEventsState.items])

    const calendarMonthLabel = React.useMemo(() => {
        return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(calendarMonth)
    }, [calendarMonth])

    const getCalendarDayLabel = (dayKey: string) => {
        const [year, month, day] = dayKey.split('-').map(Number)
        return new Intl.DateTimeFormat(undefined, {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        }).format(new Date(year, month - 1, day))
    }

    const calendarCells = React.useMemo(() => {
        const year = calendarMonth.getFullYear()
        const month = calendarMonth.getMonth()
        const firstDay = new Date(year, month, 1)
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        const startOffset = (firstDay.getDay() + 6) % 7
        const cells: Array<number | null> = Array.from({ length: startOffset }, () => null)

        for (let day = 1; day <= daysInMonth; day += 1) {
            cells.push(day)
        }

        while (cells.length % 7 !== 0) {
            cells.push(null)
        }

        while (cells.length < 42) {
            cells.push(null)
        }

        return cells
    }, [calendarMonth])

    const weekdayLabels = React.useMemo(() => {
        const monday = new Date(2026, 0, 5)
        return Array.from({ length: 7 }, (_, index) => {
            const date = new Date(monday)
            date.setDate(monday.getDate() + index)
            return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(date)
        })
    }, [])

    useEffect(() => {
        if (roomDataState.id > 0) {
            setLoading(true)
            dispatch(setRoomEventsData({ ...roomEventsState, selectedItem: {} as RoomEventItem }))
            const requestParams: [number, number, string] = [roomDataState.id, pageNum, roomEventsState.filter.query];
            if (role === RoleType.ORGANIZATION) {
                requestParams.push(Number(orgId!));
            }
            getEventsRequest(...requestParams)
                .then(response => {
                    if (response.status === 200) {
                        dispatch(setRoomEventsData({ ...roomEventsState, items: response.data.results }))
                        setHasNextPage(Boolean(response.data.next))
                        setInitialLoadDone(true)
                    }
                    setLoading(false)
                })
                .catch(error => {
                    console.log(error)
                })

            // Check if user can create events
            if (roomDataState?.type === RoomType.Private && roomDataState?.room_permissions) {
                if (roomDataState.room_permissions[0].only_admins_can_create_events) {
                    switch (role) {
                        case RoleType.ORGANIZATION:
                            if (isOrgAdmin || (roomDataState?.room_moderators?.findIndex(mod => mod.id === Number(orgId) && mod.type === 'org') !== -1 || roomDataState?.room_managers?.findIndex(man => man.id === Number(orgId) && man.type === 'org') !== -1)) {
                                setCanCreateEvents(true)
                            }
                            break;
                        case RoleType.USER:
                            if (isOrgAdmin || (roomDataState?.room_moderators?.findIndex(mod => mod.id === userDataState.id && mod.type === 'user') !== -1 || roomDataState?.room_managers?.findIndex(man => man.id === userDataState.id && man.type === 'user') !== -1)) {
                                setCanCreateEvents(true)
                            }
                            break;
                    }
                } else {
                    setCanCreateEvents(true)
                }
            } else if (roomDataState?.type === RoomType.Public) {
                setCanCreateEvents(true)
            } else {
                setCanCreateEvents(false)
            }
        }
    }, [roomDataState.id])

    useEffect(() => {
        if (pageNum <= 1 || roomDataState.id === undefined) {
            return
        }

        setLoadingFiles(true)

        const requestParams: [number, number, string, number?] = [roomDataState.id, pageNum, roomEventsState.filter.query]
        if (role === RoleType.ORGANIZATION) {
            requestParams.push(Number(orgId!))
        }

        getEventsRequest(...requestParams)
            .then(response => {
                if (response.status === 200) {
                    dispatch(setRoomEventsData({
                        ...roomEventsState,
                        items: [...roomEventsState.items, ...response.data.results]
                    }))
                    setHasNextPage(Boolean(response.data.next))
                }
                setLoadingFiles(false)
            })
            .catch(error => {
                console.log(error)
                setLoadingFiles(false)
            })
    }, [pageNum])

    useEffect(() => {
        if (searchParams.get('event') && intialLoadDone) {
            const eventId = parseInt(searchParams.get('event')!)
            const selectedEvent = roomEventsState.items.find((event: RoomEventItem) => event.id === eventId)
            if (selectedEvent) {
                dispatch(setRoomEventsData({ ...roomEventsState, selectedItem: selectedEvent }))
                dispatch(setModalData({ open: true, roomEventModal: true }))
            } else {
                navigate("/not-found")
            }
        }
    }, [roomEventsState.items])

    useEffect(() => {
        if (loadingFiles || roomDataState.id === undefined) return
        const handler = setTimeout(() => {
            setLoadingFiles(true)
            setPageNum(1)
            const requestParams: [number, number, string, number?] = [roomDataState.id, pageNum, roomEventsState.filter.query];
            if (role === RoleType.ORGANIZATION) {
                requestParams.push(orgId!);
            }
            getEventsRequest(...requestParams)
                .then(response => {
                    if (response.status === 200) {
                        dispatch(setRoomEventsData({ ...roomEventsState, items: response.data.results }))
                        setHasNextPage(Boolean(response.data.next))
                    }
                    setLoadingFiles(false)
                })
                .catch(error => {
                    console.log(error)
                })
        }, 300)

        return () => {
            clearTimeout(handler)
        }
    }, [roomEventsState.filter])

    useEffect(() => {
        if (role === RoleType.ORGANIZATION) {
            if (roomDataState?.organization?.id === Number(orgId)) {
                HTTPService.get(`/role-in-org/${orgId}/`, headers).then(response => {
                    if (response.status === 200) {
                        setIsOrgAdmin(response.data.is_admin)
                    }
                })
            }
        } else {
            HTTPService.get(`/role-in-org/${roomDataState?.organization?.id}/`, headers).then(response => {
                if (response.status === 200) {
                    setIsOrgAdmin(response.data.is_admin)
                }
            })
        }
    }, [roomDataState.organization?.id])


    const onCreateEventClick = () => {
        if (canCreateEvents === false) {
            dispatch(setModalData({ open: true, onlyAdminsCanCreateEvents: true }))
        } else {
            dispatch(setModalData({ open: true, roomCreateEventModal: { create: true } }))
        }
    }

    const onOpenEventDetails = (event: RoomEventItem) => {
        dispatch(setRoomEventsData({ ...roomEventsState, selectedItem: event }))
        dispatch(setModalData({ open: true, roomEventModal: true }))
    }

    const onPrevCalendarMonth = () => {
        setCalendarMonth((previousMonth) => new Date(previousMonth.getFullYear(), previousMonth.getMonth() - 1, 1))
    }

    const onNextCalendarMonth = () => {
        setCalendarMonth((previousMonth) => new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 1))
    }

    const onCalendarDayBadgeClick = (dayEvents: RoomEventItem[], dayKey: string, isExpandedDay: boolean) => {
        if (dayEvents.length === 0) {
            return
        }

        if (dayEvents.length === 1) {
            onOpenEventDetails(dayEvents[0])
            return
        }

        if (window.innerWidth <= 768) {
            setMobileSelectedCalendarDay((previousDay) => previousDay === dayKey ? null : dayKey)
            return
        }

        setExpandedCalendarDay(isExpandedDay ? null : dayKey)
    }

    const onCalendarDayCellClick = (dayEvents: RoomEventItem[], dayKey: string, isExpandedDay: boolean) => {
        if (dayEvents.length === 0) {
            return
        }

        onCalendarDayBadgeClick(dayEvents, dayKey, isExpandedDay)
    }

    useEffect(() => {
        setExpandedCalendarDay(null)
        setMobileSelectedCalendarDay(null)
    }, [calendarMonth, viewMode])

    if (roomDataState.type === RoomType.Private && (isMemberInRoom === "pending" || isMemberInRoom === "non-member")) {
        return <div className="room-layout__right-panel-content">
            <div className="room-layout__no-access">{translation.cantSeeRoomContent}</div>
        </div>
    }

    return (
        <div className="room-events__container">
            {role === RoleType.ORGANIZATION && (
                <TutorialBubble text={translation.tutorial_org_room_events} targetSelector='[data-tutorial-anchor="room-events"]' placement="bottom" visible={roomDataState.organization?.id === organizationDataState.id && organizationDataState.tutorial_progress === 6} hasNext={true} routeOnNext={`/organization-dashboard/${organizationDataState.id}`} />
            )}

            <div className='room-events-header' data-tutorial-anchor="room-events">
                <div className='room-events-header__actions'>
                    <div className='room-events-header__create-button' onClick={onCreateEventClick}>
                        <Button
                            className="button button--border-radius-05 button--color-gold button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                        >
                            {translation.create_event}
                        </Button>

                    </div>
                    <div className='room-events-header__search-input'>
                        <SearchInput
                            onRemoveIconClick={() => dispatch(setRoomEventsData({ ...roomEventsState, filter: { ...roomEventsState.filter, query: '' } }))}
                            searchvalue={roomEventsState.filter.query}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                dispatch(setRoomEventsData({ ...roomEventsState, filter: { ...roomEventsState.filter, query: event.target.value } }))
                            }}
                            isGlobalSearch={false}
                            isChatSearch={false}
                        />
                    </div>
                    <div className="room-events-header__view-toggle">
                        <button
                            type="button"
                            className={`room-events-header__view-button ${viewMode === 'list' ? 'room-events-header__view-button--active' : ''}`}
                            onClick={() => setViewMode('list')}
                        >
                            {translation.roomEventsListView}
                        </button>
                        <button
                            type="button"
                            className={`room-events-header__view-button ${viewMode === 'calendar' ? 'room-events-header__view-button--active' : ''}`}
                            onClick={() => setViewMode('calendar')}
                        >
                            {translation.roomEventsCalendarView}
                        </button>
                    </div>
                </div>
            </div>
            <div className={`room-events__content ${viewMode === 'calendar' ? 'room-events__content--calendar' : ''}`}>
                {loading ? (
                    <div className="room-events__loading">{translation.loading}</div>
                ) : (
                    <>
                        {roomEventsState.items.length > 0 && viewMode === 'list' ? (
                            <div className="room-events__list">
                                {roomEventsState.items.map((event: RoomEventItem) => (
                                    <RoomEventListItem key={event.id} event={event} />
                                ))}
                            </div>
                        ) : null}

                        {roomEventsState.items.length > 0 && viewMode === 'calendar' ? (
                            <div className="room-events-calendar">
                                <div className="room-events-calendar__header">
                                    <button type="button" className="room-events-calendar__nav" onClick={onPrevCalendarMonth}>
                                        &lt;
                                    </button>
                                    <div className="room-events-calendar__month">{calendarMonthLabel}</div>
                                    <button type="button" className="room-events-calendar__nav" onClick={onNextCalendarMonth}>
                                        &gt;
                                    </button>
                                </div>

                                <div className="room-events-calendar__weekdays">
                                    {weekdayLabels.map((weekdayLabel) => (
                                        <div key={weekdayLabel} className="room-events-calendar__weekday">{weekdayLabel}</div>
                                    ))}
                                </div>

                                <div className="room-events-calendar__grid">
                                    {calendarCells.map((day, index) => {
                                        if (day === null) {
                                            return <div key={`empty-${index}`} className="room-events-calendar__cell room-events-calendar__cell--empty" />
                                        }

                                        const dayDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day)
                                        const dayKey = toDateKey(dayDate)
                                        const dayEvents = calendarEventsByDay.get(dayKey) ?? []
                                        const isExpandedDay = expandedCalendarDay === dayKey
                                        const isMobileSelectedDay = mobileSelectedCalendarDay === dayKey
                                        const visibleDayEvents = isExpandedDay ? dayEvents : dayEvents.slice(0, 2)

                                        return (
                                            <div
                                                key={dayKey}
                                                className={`room-events-calendar__cell ${dayEvents.length > 0 ? 'room-events-calendar__cell--has-events' : ''} ${isExpandedDay ? 'room-events-calendar__cell--expanded' : ''}`}
                                                onClick={() => onCalendarDayCellClick(dayEvents, dayKey, isExpandedDay)}
                                            >
                                                <div className="room-events-calendar__day-header">
                                                    <div className="room-events-calendar__day-number">{day}</div>
                                                    {dayEvents.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className={`room-events-calendar__day-count ${isMobileSelectedDay ? 'room-events-calendar__day-count--active' : ''}`}
                                                            onClick={(event) => {
                                                                event.stopPropagation()
                                                                onCalendarDayBadgeClick(dayEvents, dayKey, isExpandedDay)
                                                            }}
                                                        >
                                                            {dayEvents.length}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="room-events-calendar__events">
                                                    {visibleDayEvents.map((event) => (
                                                        <button
                                                            type="button"
                                                            key={event.id}
                                                            className="room-events-calendar__event"
                                                            onClick={(clickEvent) => {
                                                                clickEvent.stopPropagation()
                                                                onOpenEventDetails(event)
                                                            }}
                                                        >
                                                            {event.title}
                                                        </button>
                                                    ))}
                                                    {dayEvents.length > 2 && (
                                                        <button
                                                            type="button"
                                                            className="room-events-calendar__more"
                                                            onClick={(clickEvent) => {
                                                                clickEvent.stopPropagation()
                                                                setExpandedCalendarDay(isExpandedDay ? null : dayKey)
                                                            }}
                                                        >
                                                            {isExpandedDay
                                                                ? translation.roomEventsCalendarLess
                                                                : `+${dayEvents.length - 2} ${translation.roomEventsCalendarMore}`}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>

                            </div>
                        ) : null}

                        {roomEventsState.items.length === 0 ? (
                            <div className="room-events__empty-state">
                                <div className="room-events__empty-icon">{hasSearchQuery ? '...' : <Calendar />}</div>
                                <div className="room-events__empty-copy">
                                    <h3 className="room-events__empty-title">
                                        {hasSearchQuery ? translation.roomEventsEmptySearchTitle : translation.roomEventsEmptyTitle}
                                    </h3>
                                    <p className="room-events__empty-text">
                                        {hasSearchQuery ? translation.roomEventsEmptySearchText : translation.roomEventsEmptyText}
                                    </p>
                                </div>
                                {!hasSearchQuery && canCreateEvents && (
                                    <div className="room-events__empty-action">
                                        <Button
                                            className="button button--border-radius-05 button--color-gold button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-small button--font-weight-400"
                                            onClick={onCreateEventClick}
                                        >
                                            {translation.roomEventsEmptyAction}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {hasNextPage && (
                            <div className="room-events__load-more-container">
                                <button
                                    className="room-events__load-more"
                                    onClick={() => setPageNum((prevPage) => prevPage + 1)}
                                >
                                    {translation.load_more}
                                </button>
                            </div>
                        )}

                        {mobileSelectedCalendarDay && (
                            <div className="room-events-calendar-overlay">
                                <button
                                    type="button"
                                    className="room-events-calendar-overlay__backdrop"
                                    onClick={() => setMobileSelectedCalendarDay(null)}
                                />
                                <div className="room-events-calendar-overlay__panel">
                                    <div className="room-events-calendar-overlay__header">
                                        <div className="room-events-calendar-overlay__title">
                                            {getCalendarDayLabel(mobileSelectedCalendarDay)}
                                        </div>
                                        <button
                                            type="button"
                                            className="room-events-calendar-overlay__close"
                                            onClick={() => setMobileSelectedCalendarDay(null)}
                                        >
                                            x
                                        </button>
                                    </div>
                                    <div className="room-events-calendar-overlay__list">
                                        {(calendarEventsByDay.get(mobileSelectedCalendarDay) ?? []).map((event) => (
                                            <button
                                                type="button"
                                                key={event.id}
                                                className="room-events-calendar-overlay__item"
                                                onClick={() => {
                                                    onOpenEventDetails(event)
                                                    setMobileSelectedCalendarDay(null)
                                                }}
                                            >
                                                {event.title}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default RoomEvents